import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckCircle2, Circle, ChevronRight, FileCode, CheckSquare, Info, FileText, Download, Brain, Clock } from 'lucide-react';
import { formatLastAnalysisDate } from '../utils/dateFormatter';
import { useLexLastSync } from '../contexts/LexLastSyncContext';

interface ProcedureClonePRIsMEModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface EtapeDetail {
  titre: string;
  description: string;
  commandes?: string[];
  notes?: string[];
  liens?: { label: string; url: string }[];
}

interface Etape {
  id: number;
  titre: string;
  description: string;
  detail?: EtapeDetail;
}

export default function ProcedureClonePRIsMEModal({ isOpen, onClose }: ProcedureClonePRIsMEModalProps) {
  const [activeTab, setActiveTab] = useState<'checklist' | 'prompt' | 'document'>('checklist');
  const [expandedEtapes, setExpandedEtapes] = useState<Set<number>>(new Set());
  const [completedEtapes, setCompletedEtapes] = useState<Set<number>>(new Set());
  const { dates: lexDates } = useLexLastSync();
  const lastAnalysisDate = isOpen ? (lexDates['procedure_clone_prisme_client_last_ai_analysis'] ?? null) : null;

  const toggleEtape = (id: number) => {
    const newExpanded = new Set(expandedEtapes);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedEtapes(newExpanded);
  };

  const toggleComplete = (id: number) => {
    const newCompleted = new Set(completedEtapes);
    if (newCompleted.has(id)) {
      newCompleted.delete(id);
    } else {
      newCompleted.add(id);
    }
    setCompletedEtapes(newCompleted);
  };

  const etapes: Etape[] = [
    {
      id: 1,
      titre: "Créer un répertoire localement",
      description: "Créer un répertoire localement sur le poste dans votre répertoire d'application CURSOR nommer \"PRIsME_NOMCLIENT\".",
      detail: {
        titre: "Création du répertoire local",
        description: "Créez un nouveau dossier dans votre répertoire d'applications Cursor pour le nouveau client PRIsME.",
        commandes: [
          "mkdir PRIsME_NOMCLIENT",
          "cd PRIsME_NOMCLIENT"
        ],
        notes: [
          "Remplacez NOMCLIENT par le nom réel du client (ex: PRIsME_ARCHER)",
          "Assurez-vous d'être dans le bon répertoire parent avant de créer le dossier"
        ]
      }
    },
    {
      id: 2,
      titre: "Copier le fichier .env",
      description: "À la racine du projet copier coller le fichier .env d'un autre projet déjà existant d'un autre prisme client.",
      detail: {
        titre: "Configuration du fichier .env",
        description: "Copiez le fichier .env d'un projet PRIsME client existant pour avoir une base de configuration.",
        commandes: [
          "cp ../PRIsME_AUTRECLIENT/.env .",
          "# Puis éditer le fichier .env avec les bonnes valeurs pour le nouveau client"
        ],
        notes: [
          "Le fichier .env contient les variables d'environnement nécessaires",
          "Vous devrez modifier certaines valeurs spécifiques au nouveau client"
        ]
      }
    },
    {
      id: 3,
      titre: "Associer le git ARCHER et pull",
      description: "Associer le git du client ARCHER et faire un pull à la racine du projet local https://github.com/danybourassa1977/PRIsMEARCHER.git",
      detail: {
        titre: "Clonage initial depuis ARCHER",
        description: "Associez le repository Git ARCHER pour récupérer la base de code PRIsME.",
        commandes: [
          "git remote add origin https://github.com/danybourassa1977/PRIsMEARCHER.git",
          "git pull origin main"
        ],
        notes: [
          "ARCHER est utilisé comme source de base par défaut",
          "Si vous clonez depuis un autre client, utilisez son repository à la place"
        ],
        liens: [
          { label: "Repository ARCHER", url: "https://github.com/danybourassa1977/PRIsMEARCHER.git" }
        ]
      }
    },
    {
      id: 4,
      titre: "Créer le Git repository du client",
      description: "Ensuite faire créer le Git repository du client par Dany Bourassa et demander de vous partager le Git.",
      detail: {
        titre: "Création du repository Git client",
        description: "Demandez à Dany Bourassa de créer un nouveau repository Git pour le client et de vous partager l'URL.",
        notes: [
          "Contactez Dany Bourassa pour la création du repository",
          "Le repository sera nommé selon le client (ex: PRIsME_NOMCLIENT)",
          "Attendez de recevoir l'URL du repository avant de continuer"
        ]
      }
    },
    {
      id: 5,
      titre: "Changer le git repository et push",
      description: "De votre cursor changer le git repository par celui partager par Dany et faire un git push complet.",
      detail: {
        titre: "Migration vers le nouveau repository",
        description: "Remplacez le remote Git par celui du nouveau client et poussez tout le code.",
        commandes: [
          "git remote remove origin",
          "git remote add origin https://github.com/danybourassa1977/PRIsME_NOMCLIENT.git",
          "git push -u origin main"
        ],
        notes: [
          "Remplacez l'URL par celle fournie par Dany",
          "Le push initial peut prendre du temps selon la taille du projet"
        ]
      }
    },
    {
      id: 6,
      titre: "Créer le client dans PRIsME Master",
      description: "Créer le client dans le PRIsME Master de BZ par la section Service et Direction TI par un membre du comité PRIsME et prendre en note le ID client.",
      detail: {
        titre: "Enregistrement du client dans PRIsME Master",
        description: "Un membre du comité PRIsME doit créer le client dans PRIsME Master via la section Service et Direction TI.",
        notes: [
          "Accédez à PRIsME Master → Service et Direction TI",
          "Créez un nouveau client avec les informations du client",
          "IMPORTANT : Notez le ID client généré, vous en aurez besoin pour le prompt de personnalisation",
          "Seuls les membres du comité PRIsME peuvent créer des clients"
        ]
      }
    },
    {
      id: 7,
      titre: "Créer le prompt de personnalisation",
      description: "Créer le prompt complet pour personnaliser le prisme du client avec le site web du client et son id, prendre le prompt exemple et travailler avec CHATGPT pour le demander de faire le même prompt et le personnaliser en allant lire le site web du client et lui donner le ID client et toujours lui dire que la source est ARCHER à moins d'avoir choisi de cloner une autre client.",
      detail: {
        titre: "Génération du prompt de personnalisation",
        description: "Utilisez ChatGPT pour générer un prompt personnalisé basé sur le site web du client et son ID.",
        notes: [
          "Utilisez le prompt exemple fourni dans l'onglet 'Prompt Exemple'",
          "Donnez à ChatGPT l'URL du site web du client",
          "Fournissez le ID client obtenu à l'étape 6",
          "Indiquez que la source est ARCHER (sauf si vous clonez depuis un autre client)",
          "Demandez à ChatGPT de personnaliser le prompt en lisant le site web"
        ],
        liens: [
          { label: "Voir le prompt exemple", url: "#prompt" }
        ]
      }
    },
    {
      id: 8,
      titre: "npm install",
      description: "npm install",
      detail: {
        titre: "Installation des dépendances",
        description: "Installez toutes les dépendances Node.js du projet.",
        commandes: [
          "npm install"
        ],
        notes: [
          "Cette étape peut prendre plusieurs minutes",
          "Assurez-vous d'avoir Node.js installé",
          "Vérifiez qu'il n'y a pas d'erreurs dans la sortie"
        ]
      }
    },
    {
      id: 9,
      titre: "npm audit fix --force",
      description: "npm audit fix --force",
      detail: {
        titre: "Correction des vulnérabilités",
        description: "Corrigez automatiquement les vulnérabilités de sécurité détectées.",
        commandes: [
          "npm audit fix --force"
        ],
        notes: [
          "Cette commande peut modifier certaines dépendances",
          "Vérifiez que le projet compile toujours après cette étape",
          "En cas de problème, vous pouvez faire 'npm audit fix' sans --force d'abord"
        ]
      }
    },
    {
      id: 10,
      titre: "Build",
      description: "build",
      detail: {
        titre: "Compilation du projet",
        description: "Compilez le projet pour vérifier qu'il n'y a pas d'erreurs.",
        commandes: [
          "npm run build"
        ],
        notes: [
          "Vérifiez qu'il n'y a pas d'erreurs de compilation",
          "Le build peut prendre plusieurs minutes",
          "Assurez-vous que tous les fichiers sont correctement configurés"
        ]
      }
    },
    {
      id: 11,
      titre: "Git push complet",
      description: "git push complet",
      detail: {
        titre: "Envoi des modifications",
        description: "Poussez toutes les modifications vers le repository Git du client.",
        commandes: [
          "git add .",
          "git commit -m 'Initial setup for NOMCLIENT'",
          "git push origin main"
        ],
        notes: [
          "Assurez-vous d'avoir commité toutes les modifications",
          "Vérifiez que le push s'est bien déroulé"
        ]
      }
    },
    {
      id: 12,
      titre: "Créer un projet Railway",
      description: "Créer un nouveau projet git repository sur Railway par Dany Bourassa de son compte.",
      detail: {
        titre: "Configuration Railway",
        description: "Demandez à Dany Bourassa de créer un nouveau projet sur Railway et de vous donner l'accès.",
        notes: [
          "Contactez Dany Bourassa pour la création du projet Railway",
          "Le projet sera lié au repository Git créé précédemment",
          "Attendez de recevoir l'accès au projet Railway"
        ],
        liens: [
          { label: "Railway", url: "https://railway.app" }
        ]
      }
    },
    {
      id: 13,
      titre: "Configurer les variables Railway",
      description: "Mettre les variables DATABASE_URL et NODE_ENV=production chez RAILWAY.",
      detail: {
        titre: "Variables d'environnement Railway",
        description: "Configurez les variables d'environnement nécessaires dans Railway.",
        notes: [
          "Allez dans Settings → Variables du projet Railway",
          "Ajoutez DATABASE_URL avec l'URL de la base de données",
          "Ajoutez NODE_ENV avec la valeur 'production'",
          "Vérifiez que toutes les autres variables nécessaires sont présentes"
        ]
      }
    },
    {
      id: 14,
      titre: "Renommer et configurer les URLs Railway",
      description: "Renommer le projet chez RAILWAY par PRIsME_NOMCLIENT aller configurer le URL public prismeNOMCLIENT.up.railway.app et prismeNOMCLIENT.bzhosting.ca",
      detail: {
        titre: "Configuration des domaines",
        description: "Renommez le projet et configurez les URLs publiques.",
        notes: [
          "Renommez le projet Railway en PRIsME_NOMCLIENT",
          "Configurez l'URL Railway : prismeNOMCLIENT.up.railway.app",
          "Configurez le domaine personnalisé : prismeNOMCLIENT.bzhosting.ca",
          "Assurez-vous que les deux URLs pointent vers le même service"
        ]
      }
    },
    {
      id: 15,
      titre: "Demander la création du CNAME",
      description: "Envoyer à Eric Hains les information pour faire créer le CNAME pour bzhosting.ca.",
      detail: {
        titre: "Configuration DNS",
        description: "Envoyez les informations nécessaires à Eric Hains pour créer l'enregistrement CNAME.",
        notes: [
          "Contactez Eric Hains avec les informations suivantes :",
          "- Nom du client",
          "- URL Railway : prismeNOMCLIENT.up.railway.app",
          "- Domaine souhaité : prismeNOMCLIENT.bzhosting.ca",
          "Attendez la confirmation qu'il a créé le CNAME"
        ]
      }
    },
    {
      id: 16,
      titre: "Donner les accès Git et Railway",
      description: "Donner les accès Git Hub et Railway aux Primes pour qu'ils soient autonome.",
      detail: {
        titre: "Gestion des accès",
        description: "Configurez les accès pour que les Primes puissent travailler de manière autonome.",
        notes: [
          "Dans GitHub : Ajoutez les Primes comme collaborateurs au repository",
          "Dans Railway : Ajoutez les Primes comme membres du projet",
          "Vérifiez que les Primes ont les permissions nécessaires",
          "Documentez les accès accordés"
        ]
      }
    },
    {
      id: 17,
      titre: "Mettre à jour les URLs dans PRIsME Master",
      description: "Aller mettre a jour les URL dans PRIsME Master dans les informations du client.",
      detail: {
        titre: "Mise à jour des URLs client",
        description: "Mettez à jour les URLs du client dans PRIsME Master avec les URLs finales.",
        notes: [
          "Accédez à PRIsME Master → Service et Direction TI",
          "Trouvez le client créé à l'étape 6",
          "Mettez à jour les URLs :",
          "  - URL Railway : prismeNOMCLIENT.up.railway.app",
          "  - URL personnalisée : prismeNOMCLIENT.bzhosting.ca",
          "Sauvegardez les modifications"
        ]
      }
    },
    {
      id: 18,
      titre: "Test et finalisation",
      description: "Test et finir la personnalisation du PRIsME par Cursor.",
      detail: {
        titre: "Tests et personnalisation finale",
        description: "Effectuez les tests finaux et complétez la personnalisation du PRIsME.",
        notes: [
          "Testez l'accès via les deux URLs (Railway et bzhosting.ca)",
          "Vérifiez que toutes les fonctionnalités fonctionnent",
          "Utilisez le prompt généré à l'étape 7 dans Cursor pour personnaliser",
          "Vérifiez que le client ID est correctement utilisé partout",
          "Testez la connexion à la base de données",
          "Vérifiez que les parties prenantes incluent BZ (client_id = 2)",
          "Finalisez la personnalisation selon les besoins du client"
        ]
      }
    }
  ];

  const promptExemple = `🧩 Prompt Cursor-ready — Personnalisation « Client ARCHER » → Client CHUK Radio (ID 42 + BZ ID 2)

Objectif
Adapter entièrement la section « Client ARCHER » pour devenir « Client CHUK Radio » (client_id = 42), en tenant compte du secteur : radio communautaire (CHUK 107,3 Mashteuiatsh), programmation variée 24h/24, information locale, entrevues, bingo, et diffusion en nehlueun (exclusivité régionale) ainsi qu'en français.

Conserver la structure existante, mais bannir toute référence explicite à ARCHER et à la gestion de patrimoine/finance.
Important : inclure aussi les intervenants BZ (client_id = 2) dans les filtres de parties prenantes / comité TI (BZ fait partie du comité TI via PRIsME).
Inclure une carte « Prise de besoin holistique » (obligatoire).

1) Renommage général & navigation (ARCHER → CHUK)

Renommer le composant/page ClientARCHER → ClientCHUK (ou ClientCHUKRadio) et ajuster :
- import dans App.tsx
- route /chuk (slug)
- entrée dans Sidebar.tsx : « Client CHUK Radio »
- Mettre à jour tous les imports/exports liés (ex. ComiteTIARCHER, RoadmapAnnuelARCHER, etc.) → versions CHUK.
- Examiner Accueil.tsx et toute navigation/CTA qui mentionne ARCHER pour pointer vers la nouvelle route.

2) Données contextuelles (archerData.ts → chukData.ts)

Créer chukData.ts en conservant la structure (ex. chukCards, chukContext, equipe_chuk_bz).

Tagline / baseline / promesse (ton média communautaire)
Exemples :
- « CHUK 107,3 – Radio communautaire à l'écoute de son milieu »
- « Information locale, culture et programmation 24h/24 »
- « Diffusion en nehlueun et en français, ancrée à Mashteuiatsh »

Metrics (réalistes, sans surinventer)
- Radio communautaire – Mashteuiatsh (Saguenay–Lac-Saint-Jean)
- Programmation 24h/24
- Diffusion en nehlueun (exclusivité régionale)
- Écoute en direct web + diffusion FM

Icônes suggérées : Radio, Mic, Users, Globe, ShieldCheck.

Enjeux & objectifs 2026 (média / diffusion / communauté)
- Gouvernance TI & sécurité : site web, streaming, comptes, accès, sauvegardes.
- Continuité de diffusion (pannes, redondance, procédures, monitoring).
- Gestion du contenu : programmation, archives audio, entrevues, nouvelles.
- Optimisation de l'expérience auditeur : "écoute en direct", délai, qualité streaming, compatibilité mobile.
- Monétisation/activités : pub locale, campagnes moitié-moitié, bingos, événements (process & traçabilité).
- Modernisation outils internes : calendrier émissions, workflow nouvelles, publication web.

Équipe (rôles adaptés + BZ)
- Direction / DG
- Responsable programmation
- Responsable nouvelles & entrevues
- Responsable technique diffusion / son
- Responsable marketing/ventes pub locale
- Responsable TI / web / streaming
- BZ : Conseiller TI, Architecte sécurité, PMO PRIsME

3) Page ClientCHUK.tsx

Remplacer archerContext, archerCards → chukContext, chukCards.
Personnaliser hero section (titre, baseline, promesse, métriques).
Mettre à jour « Enjeux critiques » & « Objectifs 2026 » pour refléter diffusion + contenu + continuité.
Conserver selectedCard / modales, mais corriger tous les imports vers les composants renommés.

4) Cartes & modales associées (texte radio + ID 42, et BZ=2)

A) Carte OBLIGATOIRE — Prise de besoin holistique
Ajouter/adapter une carte :
- Prise de besoin holistique – Écosystème radio (diffusion, contenu, communauté)
- Capture : FM + web, outils de studio, streaming, site web, publication nouvelles, besoins communauté, événements, pub, bingo.
- Livrable : portrait 360 + priorisation + plan d'action + quick wins + roadmap.

B) Gouvernance / Comité TI (avec inclusion BZ)
Données/API : utiliser client_id = 42 et inclure BZ client_id = 2.
Partout où tu charges parties prenantes / comité TI :
- getPartiesPrenantes([42, 2]) (ou logique équivalente).
Copywriting : comité TI orienté diffusion/continuité, cybersécurité, gestion contenus, outils internes.

C) Modules existants (BesoinsWizard, Roadmap, Générateur docs, Panorama, Référentiel)
- BesoinsWizard : clientId={42} et clientType="media_radio_communautaire".
- RoadmapAnnuel : exemples de projets :
  - Monitoring & redondance streaming/diffusion
  - Modernisation workflow nouvelles/entrevues
  - Archivage audio & gestion droits
  - Sécurité accès & sauvegardes
  - Amélioration UX écoute en direct (mobile/latence)
- GenerateurDocuments (renommer) :
  - « Plan directeur TI – CHUK Radio »
  - « Roadmap 2026 – Diffusion & contenu »
  - « Plan de continuité diffusion – CHUK »
  - « Cadre gouvernance TI – CHUK »
  - « Rapport KPI – audience / diffusion / opérations »

5) Services & API (DEFAULT_CLIENT_ID + inclusion BZ)

Dans archerApiService.ts (ou équivalent), remplacer les défauts :
\`\`\`
const DEFAULT_CLIENT_ID = 42;
const BZ_CLIENT_ID = 2;
\`\`\`

S'assurer que tous les appels utilisent 42.
"Parties prenantes / comité" doit supporter [42,2] (sinon ajouter un param includeClientIds).

6) Contenu transversal (recherche globale "ARCHER")

Grep/ripgrep sur "ARCHER" et remplacer par "CHUK Radio" / "CHUK".
Retirer tout vocabulaire finance/patrimoine → radio, diffusion, programmation, nouvelles, communauté.
Mettre à jour aussi PanoramaData, GenerateurDocuments, BesoinsWizardSummary, README.

7) Ton & terminologie (radio communautaire)

Vocabulaire : diffusion, programmation, studio, entrevues, nouvelles, auditeurs, streaming, continuité.
Exemples :
- "Cockpit ARCHER360" → "Cockpit diffusion & opérations – CHUK"
- "Portefeuille client" → "Portefeuille d'émissions & contenus"
- "Actifs financiers" → "Actifs médias (archives audio, équipements studio, site web)"

8) Vérifications finales

- Route /chuk OK, Sidebar OK.
- Aucune mention restante d'ARCHER.
- Tous les appels utilisent client_id = 42.
- Parties prenantes / comité TI incluent CHUK (42) + BZ (2).
- Carte "Prise de besoin holistique" présente.
- Documenter dans README interne :
  - Client = CHUK Radio (CHUK 107,3 Mashteuiatsh)
  - client_id = 42
  - BZ client_id = 2
  - slug = /chuk
  - secteur = radio communautaire (diffusion FM + web, contenu local, nehlueun + français)`;

  if (!isOpen) return null;

  const progress = (completedEtapes.size / etapes.length) * 100;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50"
          />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-br from-slate-900 via-indigo-900 to-slate-900 rounded-2xl w-[90vw] h-[90vh] border border-indigo-500/30 shadow-2xl shadow-indigo-500/20 flex flex-col">
              {/* Header */}
              <div className="relative p-4 border-b border-indigo-500/30 bg-gradient-to-r from-indigo-900/50 to-purple-900/50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-lg">
                      <FileCode className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <div className="flex items-center gap-3 mb-1">
                        <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-400">
                          Procédure Clone PRIsME Client
                        </h2>
                        <span className="flex items-center gap-1.5 px-3 py-1.5 backdrop-blur-sm bg-white/20 border border-white/30 rounded-full text-xs font-semibold text-white" title="Disponible dans AI Assistant">
                          <Brain className="w-4 h-4 text-yellow-200" />
                          AI
                        </span>
                      </div>
                      <p className="text-gray-400 text-sm">
                        Guide étape par étape pour cloner un PRIsME client
                      </p>
                      {lastAnalysisDate && (
                        <p className="text-white/70 text-xs mt-2 flex items-center gap-1.5">
                          <Clock className="w-3.5 h-3.5" />
                          Dernière synchro LEX: {formatLastAnalysisDate(lastAnalysisDate)}
                        </p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={onClose}
                    className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-400" />
                  </button>
                </div>
              </div>

              {/* Tabs Navigation */}
              <div className="border-b border-indigo-500/30 bg-slate-900/50 px-8 pt-4 pb-0">
                <div className="flex gap-2">
                  <button
                    onClick={() => setActiveTab('checklist')}
                    className={`flex items-center gap-2 px-6 py-3 rounded-t-lg font-semibold transition-all ${
                      activeTab === 'checklist'
                        ? 'bg-gradient-to-b from-indigo-600 to-purple-600 text-white shadow-lg'
                        : 'text-gray-400 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    <CheckSquare className="w-4 h-4" />
                    Checklist Procédure
                  </button>
                  <button
                    onClick={() => setActiveTab('prompt')}
                    className={`flex items-center gap-2 px-6 py-3 rounded-t-lg font-semibold transition-all ${
                      activeTab === 'prompt'
                        ? 'bg-gradient-to-b from-indigo-600 to-purple-600 text-white shadow-lg'
                        : 'text-gray-400 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    <FileCode className="w-4 h-4" />
                    Prompt Exemple
                  </button>
                  <button
                    onClick={() => setActiveTab('document')}
                    className={`flex items-center gap-2 px-6 py-3 rounded-t-lg font-semibold transition-all ${
                      activeTab === 'document'
                        ? 'bg-gradient-to-b from-indigo-600 to-purple-600 text-white shadow-lg'
                        : 'text-gray-400 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    <FileText className="w-4 h-4" />
                    Document PDF
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-8 bg-gradient-to-b from-slate-900 to-slate-950">
                <AnimatePresence mode="wait">
                  {activeTab === 'checklist' && (
                    <motion.div
                      key="checklist"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.3 }}
                      className="space-y-6"
                    >
                      {/* Progress Bar */}
                      <div className="bg-white/5 rounded-xl p-6 border border-indigo-500/20">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-300">Progression</span>
                          <span className="text-sm font-bold text-indigo-400">
                            {completedEtapes.size} / {etapes.length} étapes
                          </span>
                        </div>
                        <div className="w-full bg-gray-700 rounded-full h-3">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${progress}%` }}
                            className="bg-gradient-to-r from-indigo-500 to-purple-500 h-3 rounded-full"
                          />
                        </div>
                      </div>

                      {/* Checklist */}
                      <div className="space-y-3">
                        {etapes.map((etape) => {
                          const isExpanded = expandedEtapes.has(etape.id);
                          const isCompleted = completedEtapes.has(etape.id);
                          const Icon = isCompleted ? CheckCircle2 : Circle;

                          return (
                            <div
                              key={etape.id}
                              className={`bg-white/5 rounded-xl border-2 transition-all ${
                                isCompleted
                                  ? 'border-green-500/50 bg-green-500/10'
                                  : 'border-indigo-500/30 hover:border-indigo-500/50'
                              }`}
                            >
                              <div className="p-4">
                                <div className="flex items-start gap-4">
                                  <button
                                    onClick={() => toggleComplete(etape.id)}
                                    className={`mt-1 flex-shrink-0 ${
                                      isCompleted ? 'text-green-400' : 'text-gray-400 hover:text-indigo-400'
                                    } transition-colors`}
                                  >
                                    <Icon className="w-6 h-6" />
                                  </button>
                                  <div className="flex-1">
                                    <div className="flex items-start justify-between gap-4">
                                      <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-2">
                                          <span className="bg-indigo-500/20 text-indigo-300 px-2 py-1 rounded text-xs font-bold">
                                            Étape {etape.id}
                                          </span>
                                          {isCompleted && (
                                            <span className="bg-green-500/20 text-green-300 px-2 py-1 rounded text-xs font-semibold">
                                              Complétée
                                            </span>
                                          )}
                                        </div>
                                        <h3 className={`text-lg font-bold mb-2 ${
                                          isCompleted ? 'text-green-300 line-through' : 'text-white'
                                        }`}>
                                          {etape.titre}
                                        </h3>
                                        <p className="text-gray-300 text-sm leading-relaxed">
                                          {etape.description}
                                        </p>
                                      </div>
                                      {etape.detail && (
                                        <button
                                          onClick={() => toggleEtape(etape.id)}
                                          className="flex items-center gap-2 px-4 py-2 bg-indigo-500/20 hover:bg-indigo-500/30 text-indigo-300 rounded-lg transition-all text-sm font-semibold"
                                        >
                                          <Info className="w-4 h-4" />
                                          Détail
                                          <ChevronRight
                                            className={`w-4 h-4 transition-transform ${
                                              isExpanded ? 'rotate-90' : ''
                                            }`}
                                          />
                                        </button>
                                      )}
                                    </div>

                                    {/* Detail Section */}
                                    {isExpanded && etape.detail && (
                                      <motion.div
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: 'auto' }}
                                        exit={{ opacity: 0, height: 0 }}
                                        className="mt-4 pt-4 border-t border-indigo-500/20"
                                      >
                                        <h4 className="text-md font-bold text-indigo-300 mb-2">
                                          {etape.detail.titre}
                                        </h4>
                                        <p className="text-gray-300 text-sm mb-4 leading-relaxed">
                                          {etape.detail.description}
                                        </p>

                                        {etape.detail.commandes && etape.detail.commandes.length > 0 && (
                                          <div className="mb-4">
                                            <h5 className="text-sm font-semibold text-indigo-400 mb-2">Commandes :</h5>
                                            <div className="bg-slate-800 rounded-lg p-4 border border-indigo-500/20">
                                              <pre className="text-xs text-gray-300 font-mono">
                                                {etape.detail.commandes.map((cmd, idx) => (
                                                  <div key={idx} className="mb-1">
                                                    {cmd}
                                                  </div>
                                                ))}
                                              </pre>
                                            </div>
                                          </div>
                                        )}

                                        {etape.detail.notes && etape.detail.notes.length > 0 && (
                                          <div className="mb-4">
                                            <h5 className="text-sm font-semibold text-indigo-400 mb-2">Notes importantes :</h5>
                                            <ul className="space-y-2">
                                              {etape.detail.notes.map((note, idx) => (
                                                <li key={idx} className="flex items-start gap-2 text-sm text-gray-300">
                                                  <span className="text-indigo-400 mt-1">•</span>
                                                  <span>{note}</span>
                                                </li>
                                              ))}
                                            </ul>
                                          </div>
                                        )}

                                        {etape.detail.liens && etape.detail.liens.length > 0 && (
                                          <div>
                                            <h5 className="text-sm font-semibold text-indigo-400 mb-2">Liens utiles :</h5>
                                            <div className="flex flex-wrap gap-2">
                                              {etape.detail.liens.map((lien, idx) => {
                                                // Si l'URL commence par "#", c'est un lien interne pour changer d'onglet
                                                if (lien.url.startsWith('#')) {
                                                  const tabName = lien.url.substring(1); // Enlever le "#"
                                                  return (
                                                    <button
                                                      key={idx}
                                                      onClick={(e) => {
                                                        e.preventDefault();
                                                        setActiveTab(tabName as 'checklist' | 'prompt' | 'document');
                                                      }}
                                                      className="px-3 py-1 bg-indigo-500/20 hover:bg-indigo-500/30 text-indigo-300 rounded-lg text-sm transition-all"
                                                    >
                                                      {lien.label}
                                                    </button>
                                                  );
                                                }
                                                // Sinon, c'est un lien externe normal
                                                return (
                                                  <a
                                                    key={idx}
                                                    href={lien.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="px-3 py-1 bg-indigo-500/20 hover:bg-indigo-500/30 text-indigo-300 rounded-lg text-sm transition-all"
                                                  >
                                                    {lien.label}
                                                  </a>
                                                );
                                              })}
                                            </div>
                                          </div>
                                        )}
                                      </motion.div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}

                  {activeTab === 'prompt' && (
                    <motion.div
                      key="prompt"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.3 }}
                      className="space-y-6"
                    >
                      <div className="bg-gradient-to-r from-indigo-600/20 via-purple-600/20 to-indigo-600/20 rounded-xl p-6 border-2 border-indigo-500/50">
                        <h3 className="text-2xl font-bold text-center text-white mb-2">
                          Prompt Exemple pour Personnalisation
                        </h3>
                        <p className="text-gray-300 text-center text-sm">
                          Utilisez ce prompt comme base pour générer un prompt personnalisé avec ChatGPT
                        </p>
                      </div>

                      <div className="bg-slate-800 rounded-xl p-6 border border-indigo-500/20">
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="text-lg font-bold text-white">Prompt Cursor-ready</h4>
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(promptExemple);
                              alert('Prompt copié dans le presse-papier !');
                            }}
                            className="px-4 py-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg text-sm font-semibold transition-all"
                          >
                            Copier
                          </button>
                        </div>
                        <pre className="text-xs text-gray-300 font-mono whitespace-pre-wrap overflow-x-auto">
                          {promptExemple}
                        </pre>
                      </div>

                      <div className="bg-yellow-500/10 rounded-xl p-6 border border-yellow-500/30">
                        <h4 className="text-lg font-bold text-yellow-300 mb-3 flex items-center gap-2">
                          <Info className="w-5 h-5" />
                          Instructions d'utilisation
                        </h4>
                        <ul className="space-y-2 text-gray-300 text-sm">
                          <li className="flex items-start gap-2">
                            <span className="text-yellow-400 mt-1">•</span>
                            <span>Copiez ce prompt et donnez-le à ChatGPT</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-yellow-400 mt-1">•</span>
                            <span>Fournissez l'URL du site web du client à ChatGPT</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-yellow-400 mt-1">•</span>
                            <span>Donnez le ID client obtenu lors de la création dans PRIsME Master</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-yellow-400 mt-1">•</span>
                            <span>Demandez à ChatGPT de personnaliser le prompt en lisant le site web</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-yellow-400 mt-1">•</span>
                            <span>Utilisez le prompt généré dans Cursor pour personnaliser le PRIsME</span>
                          </li>
                        </ul>
                      </div>
                    </motion.div>
                  )}

                  {activeTab === 'document' && (
                    <motion.div
                      key="document"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.3 }}
                      className="space-y-6"
                    >
                      <div className="bg-gradient-to-r from-indigo-600/20 via-purple-600/20 to-indigo-600/20 rounded-xl p-6 border-2 border-indigo-500/50">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="text-2xl font-bold text-center text-white mb-2">
                              Document Source - Procédure Pro BZ
                            </h3>
                            <p className="text-gray-300 text-center text-sm">
                              Document PDF officiel de la procédure de clonage PRIsME Client
                            </p>
                          </div>
                          <button
                            onClick={() => {
                              const link = document.createElement('a');
                              link.href = '/docs/Procedure_Pro_BZ_NouveauClient_Prisme_v1.0.pdf';
                              link.download = 'Procedure_Pro_BZ_NouveauClient_Prisme_v1.0.pdf';
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                            }}
                            className="group px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white rounded-xl transition-all duration-200 font-bold shadow-lg shadow-indigo-500/20 hover:shadow-indigo-500/40 hover:scale-105 flex items-center gap-2 border border-indigo-400/30"
                          >
                            <Download className="w-5 h-5 group-hover:animate-pulse" />
                            <span>Télécharger PDF</span>
                          </button>
                        </div>
                      </div>

                      <div className="bg-white/5 rounded-xl p-6 border border-indigo-500/20">
                        <div className="w-full h-[calc(90vh-300px)] min-h-[600px] border-2 border-indigo-500/30 rounded-lg overflow-hidden bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center">
                          <div className="flex flex-col items-center justify-center p-12 text-center max-w-2xl">
                            <div className="mb-6 p-6 bg-indigo-500/20 rounded-full">
                              <FileText className="w-24 h-24 text-indigo-400" />
                            </div>
                            <h4 className="text-2xl font-bold text-white mb-4">
                              Document PDF - Procédure Pro BZ
                            </h4>
                            <p className="text-gray-300 mb-6 text-lg leading-relaxed">
                              Cliquez sur le bouton ci-dessous pour ouvrir le document PDF complet dans un nouvel onglet de votre navigateur.
                            </p>
                            <a
                              href="/docs/Procedure_Pro_BZ_NouveauClient_Prisme_v1.0.pdf"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="group px-8 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white rounded-xl transition-all duration-200 font-bold shadow-lg shadow-indigo-500/20 hover:shadow-indigo-500/40 hover:scale-105 flex items-center gap-3 border border-indigo-400/30"
                            >
                              <FileText className="w-6 h-6 group-hover:animate-pulse" />
                              <span>Ouvrir le PDF dans un nouvel onglet</span>
                            </a>
                            <p className="text-gray-400 text-sm mt-6">
                              Le document s'ouvrira dans votre lecteur PDF intégré au navigateur
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="bg-indigo-500/10 rounded-xl p-4 border border-indigo-500/30">
                        <p className="text-sm text-indigo-300">
                          <strong>Astuce :</strong> Vous pouvez également utiliser le bouton "Télécharger PDF" en haut pour sauvegarder le document sur votre ordinateur.
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Footer */}
              <div className="p-6 border-t border-indigo-500/30 bg-slate-900/50">
                <div className="flex items-center justify-end">
                  <button
                    onClick={onClose}
                    className="px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-xl hover:from-indigo-600 hover:to-purple-600 transition-all duration-200 font-semibold shadow-lg shadow-indigo-500/20 hover:shadow-indigo-500/40 hover:scale-105"
                  >
                    Fermer
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

